import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  collapseTextChangeRangesAcrossMultipleVersions,
  isConstructorDeclaration,
} from "typescript";

//JS classes
import { ApiAdapter } from "../../utils/adapters";
import { GoalRouter } from "../classes/goal_router";

//custom hooks
import { useDB } from "../../utils/custom_hooks/UseDB";
import { TMDataContext } from "../../utils/custom_hooks/TMDataContext";
import { useTranslation } from "react-i18next";
import { useStatus } from "../../utils/custom_hooks/UseStatus";

//primary containers
import { InterviewContainer } from "../interview_goals/InterviewContainer";
import { ObservationContainer } from "../observations_goals/ObservationContainer";
import { EducationContainer } from "../education_goals/EducationContainer";

//component utilities
import { NoSelection } from "./components/generalUtilities";
import GoalHomepage from "../homepage/GoalHomepage";

import {
  GoalsIndexPropsShape,
  routerObjectShape,
} from "../interfaces/componentTypes";
import { SetupHandler, TemplateHandler } from "../../utils/state_handlers";
import { pageListeners } from "../../utils/listeners";
import { apiAdapterShape } from "../interfaces/classTypes";
import { useTemplateManager } from "../../utils/custom_hooks/UseTemplateManager";
import { TemplateDataCourier } from "./components/TemplateDataCourier";

const GoalsIndex: React.FC<GoalsIndexPropsShape> = ({
  current_program_id,
  config_settings,
}) => {
  const [pageStatus, setPageStatus] = useStatus({
    loading: false,
    loadFail: false,
    noTemplates: false,
    unsavedProgram: false,
    uploadFail: false,
  });

  const [api, setApi] = useState<apiAdapterShape | undefined>();

  const [templateSet, setTemplateSet] = useTemplateManager(null);
  const [navPage, setNavPage] = useState<routerObjectShape | null>(null);

  useEffect(() => {
    if (current_program_id == null) {
      setPageStatus("unsavedProgram", true);
    } else {
      setPageStatus("unsavedProgram", false);
    }
    setApi(new ApiAdapter());
    pageListeners.detectEditorNavigation();
  }, []);

  const [DBObject, setDBObject] = useDB({
    ConfigVariables: config_settings,
    monitorProgramId: current_program_id,
  });
  const { t, i18n } = useTranslation();

  var router = new GoalRouter(t);

  //UX|UI state error handling of scheduling tab
  const [profileInterviews, setProfileInterviews] = useState<string[]>([]);
  const [detectedChange, setDetectedChange] = useState<number>(0);

  useLayoutEffect(() => {
    pageListeners.programInterviewIsRemoved(setDetectedChange);
  }, [profileInterviews]);

  useLayoutEffect(() => {
    pageListeners.programInterviewIsAdded(setDetectedChange);

    setTimeout(() => {
      pageListeners.parseProgramInterviews(
        setProfileInterviews,
        profileInterviews
      );
    }, 100);
  }, []);

  useEffect(() => {
    pageListeners.parseProgramInterviews(
      setProfileInterviews,
      profileInterviews
    );
  }, [detectedChange]);

  //PRIMARY INFRASTRUCTURE SETUP API CALL and STATE SETTING
  useEffect(() => {
    if (!api) return;
    setPageStatus("loading", true);
    api
      .loadSetup()
      .then((payload) => new SetupHandler(payload, setDBObject))
      .then(() =>
        pageListeners.parseProgramInterviews(
          setProfileInterviews,
          profileInterviews
        )
      )
      .then(() => {
        if (pageStatus.unsavedProgram) {
          setTemplateSet("load", []);
        } else {
          api
            .loadGoalTemplates(DBObject.monitorProgramId)
            .then((goalTemplates) => new TemplateHandler(goalTemplates))
            .then((set) => setTemplateSet("load", set));
        }
      })
      .finally(() => {
        setNavPage(router.goToHomepage());
        setPageStatus("loading", false);
        setPageStatus("loadFail", false);
      })
      .catch(() => setPageStatus("loadFail", true));
  }, [api]);

  const goalCategoryRouter = () => {
    if (pageStatus.loading) return <div>{t("progress_messages.loading")}</div>;

    if (pageStatus.loadFail)
      return <div>{t("views.goals.errors.load_failure")}</div>;
    if (navPage == null) return;
    switch (navPage.containerName) {
      case "homepage":
        return (
          <GoalHomepage
            profileInterviews={profileInterviews}
            setNavPage={setNavPage}
            uploadFail={pageStatus.uploadFail}
            templateSet={templateSet}
          />
        );
        break;
      case "observationContainer":
        return <ObservationContainer />;
        break;
      case "interviewContainer":
        return <InterviewContainer />;
        break;
      case "educationContainer":
        return <EducationContainer />;
        break;
      default:
        return (
          <NoSelection
            message={t("views.goals.navigation.goal_type_select_prompt")}
          />
        );
    }
  };

  return (
    <TMDataContext.Provider
      value={{
        DBObject: DBObject,
        t: t,
        i18n: i18n,
        api: api,
        setPageStatus: setPageStatus,
        router: router,
        navPage: navPage,
        setNavPage: setNavPage,
        uuid: uuidv4,
        setTemplateSet: setTemplateSet,
        profileInterviews: profileInterviews,
      }}
    >
      {goalCategoryRouter()}
      <TemplateDataCourier templateSet={templateSet} />
    </TMDataContext.Provider>
  );
};
export default GoalsIndex;
